import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { prettyPrintDateTime, relativeLogTime } from "util/dates";

import type { ReportJob } from "./types";

import { DiagnosisDetail } from "./DiagnosisDetail";
import { categoryChipColor, confidenceChipColor } from "./diagnosisUtils";

function formatCategory(category: string): string {
  return category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatStopReason(reason: string): string {
  return reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Diagnosis = NonNullable<ReportJob["automationDiagnoses"]>[number];
type FixLoop = NonNullable<ReportJob["automationFixLoops"]>[number];
type FixLoopDiagnosis = FixLoop["diagnoses"][number];

// ---------------------------------------------------------------------------
// Diagnosis card (used for standalone initial diagnoses)
// ---------------------------------------------------------------------------

function DiagnosisCard({ d }: { d: Diagnosis }) {
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Tooltip title="Failure category">
            <Chip
              label={formatCategory(d.category)}
              color={categoryChipColor(d.category)}
              size="small"
            />
          </Tooltip>
          <Chip
            label={`${(d.confidence * 100).toFixed(0)}% confidence`}
            color={confidenceChipColor(d.confidence)}
            size="small"
            variant="outlined"
          />
          <Tooltip title={prettyPrintDateTime(d.createdAt)}>
            <Typography variant="caption" color="text.secondary">
              {relativeLogTime(d.createdAt)}
            </Typography>
          </Tooltip>
        </Stack>

        <DiagnosisDetail
          summary={d.summary}
          suggestedFix={d.suggestedFix}
          failedStep={d.failedStep}
          affectedSelector={d.affectedSelector}
          missingData={d.missingData}
          notes={d.notes}
        />

        <Divider />
        <Stack direction="row" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            <Box component="span" fontWeight={600}>
              Model:
            </Box>{" "}
            {d.model}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <Box component="span" fontWeight={600}>
              Mode:
            </Box>{" "}
            {d.mode}
          </Typography>
          {d.turnsUsed != null && (
            <Typography variant="caption" color="text.secondary">
              <Box component="span" fontWeight={600}>
                Turns:
              </Box>{" "}
              {d.turnsUsed}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            <Box component="span" fontWeight={600}>
              Cost:
            </Box>{" "}
            ${d.estimatedCost.toFixed(4)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Expandable iteration diagnosis row (used inside fix loop cards)
// ---------------------------------------------------------------------------

function IterationDiagnosisRow({ d }: { d: FixLoopDiagnosis }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        "&:before": { display: "none" },
        backgroundColor: "transparent",
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 1.5, "& .MuiAccordionSummary-content": { my: 1, overflow: "hidden" } }}
      >
        <Stack spacing={0.5} sx={{ overflow: "hidden", minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              #{d.iterationNumber}
            </Typography>
            <Tooltip title="Failure category">
              <Chip
                label={formatCategory(d.category)}
                color={categoryChipColor(d.category)}
                size="small"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Tooltip>
            <Tooltip title="Confidence">
              <Chip
                label={`${(d.confidence * 100).toFixed(0)}% confidence`}
                color={confidenceChipColor(d.confidence)}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Tooltip>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {d.summary}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0 }}>
        <DiagnosisDetail
          summary={d.summary}
          suggestedFix={d.suggestedFix}
          failedStep={d.failedStep}
          affectedSelector={d.affectedSelector}
          missingData={d.missingData}
          notes={d.notes}
        />
      </AccordionDetails>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------
// Iteration detail types (from iterationDetails JSON)
// ---------------------------------------------------------------------------

type FileEdit = {
  filePath: string;
  oldContent: string;
  newContent: string;
};

type IterationDetail = {
  iteration: number;
  diagnosis: {
    category: string;
    confidence: number;
    summary: string;
    suggestedFix: string | null;
    affectedSelector: string | null;
    failedStep: string | null;
    missingData: string | null;
    notes: string | null;
    turnsUsed?: number;
    toolsUsed?: string[];
  };
  fix: {
    edits: FileEdit[];
    summary: string;
    turnsUsed: number;
    toolsUsed: string[];
    tokenUsage?: { input: number; output: number; total: number };
    costEstimate?: { totalCost: number };
  } | null;
  rerunStatus: string | null;
  rerunError: string | null;
};

function parseIterationDetails(raw: unknown): IterationDetail[] {
  if (!Array.isArray(raw)) return [];
  return raw as IterationDetail[];
}

// ---------------------------------------------------------------------------
// Single iteration card
// ---------------------------------------------------------------------------

function IterationCard({
  iter,
  hideDiagnosis,
}: {
  iter: IterationDetail;
  hideDiagnosis?: boolean;
}) {
  const isSuccess =
    iter.rerunStatus === "SUCCEEDED" || iter.rerunStatus === "AWAITING_MANUAL_INTERVENTION";

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        "&:before": { display: "none" },
        backgroundColor: "transparent",
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 1.5, "& .MuiAccordionSummary-content": { my: 1, overflow: "hidden" } }}
      >
        <Stack spacing={0.5} sx={{ overflow: "hidden", minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              Iteration {iter.iteration}
            </Typography>
            <Tooltip title="Failure category">
              <Chip
                label={formatCategory(iter.diagnosis.category)}
                color={categoryChipColor(iter.diagnosis.category)}
                size="small"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Tooltip>
            <Tooltip title="Confidence">
              <Chip
                label={`${(iter.diagnosis.confidence * 100).toFixed(0)}% confidence`}
                color={confidenceChipColor(iter.diagnosis.confidence)}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Tooltip>
            {iter.rerunStatus && (
              <Chip
                label={iter.rerunStatus}
                color={isSuccess ? "success" : "default"}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {iter.diagnosis.summary}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0 }}>
        <Stack spacing={2}>
          {/* Diagnosis detail (hidden for iteration 1 when initial diagnosis is shown above) */}
          {!hideDiagnosis && (
            <DiagnosisDetail
              summary={iter.diagnosis.summary}
              suggestedFix={iter.diagnosis.suggestedFix}
              failedStep={iter.diagnosis.failedStep}
              affectedSelector={iter.diagnosis.affectedSelector}
              missingData={iter.diagnosis.missingData}
              notes={iter.diagnosis.notes}
            />
          )}

          {/* Fix applied */}
          {iter.fix && (
            <Box
              sx={{
                backgroundColor: "action.hover",
                borderLeft: 3,
                borderColor: "info.main",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: "block" }}>
                Fix Applied
              </Typography>
              <Typography variant="body2">{iter.fix.summary}</Typography>
              {iter.fix.edits.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Files edited: {iter.fix.edits.map((e) => e.filePath).join(", ")}
                </Typography>
              )}
            </Box>
          )}

          {/* Rerun result */}
          {iter.rerunStatus && (
            <Typography variant="caption" color="text.secondary">
              <Box component="span" fontWeight={600}>
                Rerun result:{" "}
              </Box>
              {iter.rerunStatus}
              {iter.rerunError ? ` — ${iter.rerunError}` : ""}
            </Typography>
          )}

          {/* Metadata footer */}
          <Divider />
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {iter.diagnosis.turnsUsed != null && (
                <Typography variant="caption" color="text.secondary">
                  <Box component="span" fontWeight={600}>
                    Diagnosis turns:
                  </Box>{" "}
                  {iter.diagnosis.turnsUsed}
                </Typography>
              )}
              {iter.fix && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    <Box component="span" fontWeight={600}>
                      Fix turns:
                    </Box>{" "}
                    {iter.fix.turnsUsed}
                  </Typography>
                  {iter.fix.costEstimate && (
                    <Typography variant="caption" color="text.secondary">
                      <Box component="span" fontWeight={600}>
                        Fix cost:
                      </Box>{" "}
                      ${iter.fix.costEstimate.totalCost.toFixed(4)}
                    </Typography>
                  )}
                </>
              )}
            </Stack>
            {iter.diagnosis.toolsUsed && iter.diagnosis.toolsUsed.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                <Box component="span" fontWeight={600}>
                  Diagnosis tools:
                </Box>{" "}
                {iter.diagnosis.toolsUsed.join(", ")}
              </Typography>
            )}
            {iter.fix && iter.fix.toolsUsed.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                <Box component="span" fontWeight={600}>
                  Fix tools:
                </Box>{" "}
                {iter.fix.toolsUsed.join(", ")}
              </Typography>
            )}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------
// Fix loop card
// ---------------------------------------------------------------------------

function FixLoopCard({ fl, hasInitialDiagnosis }: { fl: FixLoop; hasInitialDiagnosis: boolean }) {
  const iterations = parseIterationDetails(fl.iterationDetails);

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Tooltip title="Outcome">
            <Chip
              label={fl.success ? "Fixed" : "Not fixed"}
              color={fl.success ? "success" : "default"}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Stop reason">
            <Chip label={formatStopReason(fl.stopReason)} size="small" variant="outlined" />
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {fl.source.toUpperCase()}
          </Typography>
          <Tooltip title={prettyPrintDateTime(fl.createdAt)}>
            <Typography variant="caption" color="text.secondary">
              {relativeLogTime(fl.createdAt)}
            </Typography>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={3}>
          <Typography variant="body2">
            Iterations: {fl.iterationCount} / {fl.maxIterations}
          </Typography>
          <Typography variant="body2">Cost: ${fl.totalCost.toFixed(4)}</Typography>
        </Stack>

        {fl.prUrl && (
          <Link href={fl.prUrl} target="_blank" rel="noopener" variant="body2">
            {fl.prBranch ?? "View PR"}
          </Link>
        )}

        {/* Iteration details (preferred) or fall back to diagnosis-only view */}
        {iterations.length > 0 ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {iterations.map((iter) => (
              <IterationCard
                key={iter.iteration}
                iter={iter}
                hideDiagnosis={hasInitialDiagnosis && iter.iteration === 1}
              />
            ))}
          </Stack>
        ) : (
          fl.diagnoses.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
                Iteration Diagnoses
              </Typography>
              <Stack spacing={1}>
                {fl.diagnoses.map((d) => (
                  <IterationDiagnosisRow key={d.id} d={d} />
                ))}
              </Stack>
            </Box>
          )
        )}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main tab component
// ---------------------------------------------------------------------------

export interface AgentDiagnosisTabProps {
  diagnoses: Diagnosis[];
  fixLoops: FixLoop[];
}

export function AgentDiagnosisTab({ diagnoses, fixLoops }: AgentDiagnosisTabProps) {
  if (diagnoses.length === 0 && fixLoops.length === 0) {
    return (
      <Typography color="text.secondary">No agent diagnosis has been run for this job.</Typography>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Initial diagnosis (standalone, not part of a fix loop) */}
      {diagnoses.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={600}>
            Initial Diagnosis
          </Typography>
          {diagnoses.map((d) => (
            <DiagnosisCard key={d.id} d={d} />
          ))}
        </>
      )}

      {/* Fix loops with their iteration diagnoses */}
      {fixLoops.length > 0 && (
        <>
          {diagnoses.length > 0 && (
            <Typography variant="subtitle2" fontWeight={600}>
              Self-Healing
            </Typography>
          )}
          {fixLoops.map((fl) => (
            <FixLoopCard key={fl.id} fl={fl} hasInitialDiagnosis={diagnoses.length > 0} />
          ))}
        </>
      )}
    </Stack>
  );
}
